import * as def from '@/definitions'
import { PathLayer } from './pathLayer'
import { ColorConstruct } from '@/utilityModels/colorConstruct'
import { Layer } from './layer';
import { PathHelper } from '@/helper/pathHelper'
import { Vector2 } from '@/utilityModels/vector2';
import { SVGPathData } from 'svg-pathdata';

export class CurveLayer extends PathLayer
{
    public get layerType() : string { return "curve"; }

    public get x() : number
    {
        // 座標を得る
        return this.getPosition().x;
    }

    public set x(x : number)
    {
        // パスデータを取得
        var pathData = new SVGPathData(this.svgPath);
        // 座標を得る
        var rotatedX = this.getPosition().x;
        // セットしたいx座標との差を出す
        var deltaX = x - rotatedX;
        // 移動してパスを返す
        this.svgPath = pathData.translate(deltaX, 0).encode();
        // 制御点をシフト
        for(var i = 0 ; i < this._controlPoints.length; ++i)
        {
            this._controlPoints[i].x += deltaX;
        }
    }

    public get y() : number
    {
        // 座標を得る
        return this.getPosition().y;
    }

    public set y(y : number)
    {
        // パスデータを取得
        var pathData = new SVGPathData(this.svgPath);
        // 座標を得る
        var rotatedY = this.getPosition().y;
        // セットしたいｙ座標との差を出す
        var deltaY = y - rotatedY;
        // 移動してパスを返す
        this.svgPath = pathData.translate(0, deltaY).encode();
        // 制御点をシフト
        for(var i = 0 ; i < this._controlPoints.length; ++i)
        {
            this._controlPoints[i].y += deltaY;
        }
    }

    // ポジションを得る。
    private getPosition() : Vector2
    {
        // パスデータを取得
        var pathData = new SVGPathData(this.svgPath);
        // 外接矩形を取得
        var bounds = pathData.getBounds();
        // x,y座標を得る
        var x = bounds.minX;
        var y = bounds.minY;
        var vector = new Vector2(x, y);
        return vector;
    }

    // 操作点
    private _controlPoints : Array<Vector2> = [];
    public getAllControlPoints()
    {
        return this._controlPoints;
    }
    public getControlPoint(index : number)
    {
        return this._controlPoints[index];
    }
    public getControlPointsNumber()
    {
        return this._controlPoints.length;
    }
    public addControlPoint(x, y)
    {
        this._controlPoints.push(new Vector2(x, y));
        this.updatePath();
    }
    public insertControlPoint(index, x, y)
    {
        this._controlPoints.splice(index, 0, new Vector2(x, y));
        this.updatePath();
    }
    public removeControlPoint(index)
    {
        this._controlPoints.splice(index, 1);
        this.updatePath();
    }
    public moveControlPoint(index, x, y)
    {
        this._controlPoints[index].x = x;
        this._controlPoints[index].y = y;
        this.updatePath();
    }

    // 線色
    private _strokeColor : ColorConstruct = new ColorConstruct();
    public get strokeColor() : ColorConstruct
    {
        return this._strokeColor;
    }
    public set strokeColor(strokeColor : ColorConstruct)
    {
        this._strokeColor = strokeColor;
    }

    // 不透明度
    private _opacity : number = 100;
    public get opacity()
    {
        return this._opacity;
    }
    public set opacity(opacity : number)
    {
        this._opacity = opacity;
    }

    // 線幅
    private _strokeWidth : number = 1;
    public get strokeWidth() : number
    {
        return this._strokeWidth;
    }
    public set strokeWidth(strokeWidth : number)
    {
        this._strokeWidth = strokeWidth;
    }

    // パスの更新
    private updatePath()
    {
        if(this._controlPoints.length < 2) { return; }
        if(this._controlPoints.length == 2)
        {
            // 直線
            this.svgPath = PathHelper.createLine(this._controlPoints[0].x, this._controlPoints[0].y, this._controlPoints[1].x, this._controlPoints[1].y);
        }
        else if(this._controlPoints.length >= 3)
        {
            // スプライン曲線
            this.svgPath = PathHelper.createCatmullRomSpline(this._controlPoints);
        }
    }

    // デザイン回転のための特殊処理
    public rotateFromDesign(rotate : number, srcDesignCenterX : number, srcDesignCenterY : number, dstDesignCenterX : number, dstDesignCenterY : number)
    {
        var newPoints : Array<Vector2> = new Array();
        for(var i = 0; i < this._controlPoints.length; ++i)
        {
            var point = this._controlPoints[i];
            // 回転前の中心からのベクトル
            var srcVector = new Vector2(point.x - srcDesignCenterX, point.y - srcDesignCenterY);
            // このベクトルを指定角度回転
            var dstVector = srcVector.rotateDegrees(rotate, 0, 0);
            // 新しい中心を元に新規のpointを作成
            var dstPoint = new Vector2(dstDesignCenterX + dstVector.x, dstDesignCenterY + dstVector.y);
            newPoints.push(dstPoint);
        }

        this._controlPoints = newPoints;
        this.updatePath();
    }

    // 回転(グループの回転のための処理)
    private _pointsRotate : number = 0;
    public get rotate() { return this._pointsRotate; }
    public set rotate(rotate)
    {
        var delta = rotate - this._pointsRotate;
        this._pointsRotate = rotate;
        this.rotateFromDesign(delta, this.centerX, this.centerY, this.centerX, this.centerY);
    }

    // 幅(グループの回転のための処理)
    public get width()
    {
        var vertexes = this.getVertexes();
        return Math.sqrt((vertexes[0].x - vertexes[3].x) * (vertexes[0].x - vertexes[3].x) + (vertexes[0].y - vertexes[3].y) * (vertexes[0].y - vertexes[3].y));
    }
    public set width(width)
    {
        if(this.width == 0) { return; }

        var scale = width / this.width;
        // すべての操作点を中心で逆回転する
        var pathData = new SVGPathData(this.svgPath);
        var bounds = pathData.getBounds();
        var centerX = (bounds.minX + bounds.maxX) / 2;
        var centerY = (bounds.minY + bounds.maxY) / 2;
        var rotatedPoints : Array<Vector2> = new Array();
        for(var i = 0; i < this._controlPoints.length; ++i)
        {
            var p = new Vector2(this._controlPoints[i].x, this._controlPoints[i].y);
            var rotated = p.rotateDegrees(-this._pointsRotate, centerX, centerY);
            rotatedPoints.push(rotated);
        }

        // 回転点の外接矩形
        var rotatedBounds = PathHelper.getBoundsFromPoints(rotatedPoints);
        // すべての点について、左上からのベクトルをscale倍する
        var newPoints : Array<Vector2> = new Array();
        for(var i = 0; i < rotatedPoints.length; ++i)
        {
            var vectorFromTopLeft = new Vector2(rotatedPoints[i].x - rotatedBounds.x, rotatedPoints[i].y - rotatedBounds.y);
            var p = new Vector2(rotatedBounds.x + vectorFromTopLeft.x * scale, rotatedBounds.y + vectorFromTopLeft.y);
            // この点を回転する
            newPoints.push(p.rotateDegrees(this._pointsRotate, centerX, centerY));
        }
        // 点更新
        this._controlPoints = newPoints;
        this.updatePath();
    }

    // 高さ(グループ回転のための処理)
    public get height()
    {
        var vertexes = this.getVertexes();
        return Math.sqrt((vertexes[0].x - vertexes[1].x) * (vertexes[0].x - vertexes[1].x) + (vertexes[0].y - vertexes[1].y) * (vertexes[0].y - vertexes[1].y));
    }
    public set height(height)
    {
        if(this.height == 0) { return; }

        var scale = height / this.height;
        // すべての操作点を中心で逆回転する
        var pathData = new SVGPathData(this.svgPath);
        var bounds = pathData.getBounds();
        var centerX = (bounds.minX + bounds.maxX) / 2;
        var centerY = (bounds.minY + bounds.maxY) / 2;
        var rotatedPoints : Array<Vector2> = new Array();
        for(var i = 0; i < this._controlPoints.length; ++i)
        {
            var p = new Vector2(this._controlPoints[i].x, this._controlPoints[i].y);
            var rotated = p.rotateDegrees(-this._pointsRotate, centerX, centerY);
            rotatedPoints.push(rotated);
        }

        // 回転点の外接矩形
        var rotatedBounds = PathHelper.getBoundsFromPoints(rotatedPoints);
        // すべての点について、左上からのベクトルをscale倍する
        var newPoints : Array<Vector2> = new Array();
        for(var i = 0; i < rotatedPoints.length; ++i)
        {
            var vectorFromTopLeft = new Vector2(rotatedPoints[i].x - rotatedBounds.x, rotatedPoints[i].y - rotatedBounds.y);
            var p = new Vector2(rotatedBounds.x + vectorFromTopLeft.x, rotatedBounds.y + vectorFromTopLeft.y * scale);
            // この点を回転する
            newPoints.push(p.rotateDegrees(this._pointsRotate, centerX, centerY));
        }
        // 点更新
        this._controlPoints = newPoints;
        this.updatePath();
    }

    public get centerX()
    {
        var pathData = new SVGPathData(this.svgPath);
        var bounds = pathData.getBounds();
        var centerX = (bounds.minX + bounds.maxX) / 2;
        return centerX;
    }

    public get centerY()
    {
        var pathData = new SVGPathData(this.svgPath);
        var bounds = pathData.getBounds();
        var centerY = (bounds.minY + bounds.maxY) / 2;
        return centerY;
    }

    public getVertexes()
    {
        // すべての操作点を中心で逆回転する
        var pathData = new SVGPathData(this.svgPath);
        var bounds = pathData.getBounds();
        var centerX = (bounds.minX + bounds.maxX) / 2;
        var centerY = (bounds.minY + bounds.maxY) / 2;
        var rotatedPoints : Array<Vector2> = new Array();
        for(var i = 0; i < this._controlPoints.length; ++i)
        {
            var p = new Vector2(this._controlPoints[i].x, this._controlPoints[i].y);
            var rotated = p.rotateDegrees(-this._pointsRotate, centerX, centerY);
            rotatedPoints.push(rotated);
        }

        // 逆回転した点の外接矩形
        var rotatedBounds = PathHelper.getBoundsFromPoints(rotatedPoints);

        // 外接矩形の各点を正回転したものが頂点
        var vertexes : Array<Vector2> = new Array();
        var boundsPoints = [ 
            new Vector2(rotatedBounds.x, rotatedBounds.y), 
            new Vector2(rotatedBounds.x, rotatedBounds.y + rotatedBounds.height),
            new Vector2(rotatedBounds.x + rotatedBounds.width, rotatedBounds.y + rotatedBounds.height),
            new Vector2(rotatedBounds.x + rotatedBounds.width, rotatedBounds.y)
        ];
        for(var i = 0; i < boundsPoints.length; ++i)
        {
            vertexes.push(boundsPoints[i].rotateDegrees(this._pointsRotate, centerX, centerY));
        }
        return vertexes;
    }

    // 作成
    public static create(x1 : number, y1 : number, x2 : number, y2 : number, x3 : number, y3 : number)
    {
        var layer = new CurveLayer();
        // 操作点追加
        layer.addControlPoint(x1, y1);
        layer.addControlPoint(x2, y2);
        layer.addControlPoint(x3, y3);
        layer.strokeColor = new ColorConstruct(0, 0, 0);
        layer.strokeWidth = 1.0;

        return layer;
    }

    // このレイヤーに触られている
    public isTouched : boolean = false;
    public isPointContained(x : number, y : number) : boolean
    {
        return this.isTouched;
    }

    // 最後に触れた曲線のindex
    public lastTouchedIndex : number = 0;

    // 複製
    public clone()
    {
        var cloned = new CurveLayer();
        this.cloneParameter(cloned);
        return cloned;
    }

    public cloneParameter(layer : CurveLayer)
    {
        for(var i = 0; i < this._controlPoints.length; ++i)
        {
            layer._controlPoints.push(new Vector2(this._controlPoints[i].x, this._controlPoints[i].y));
        }
        layer.opacity = this._opacity;
        layer.strokeColor = this._strokeColor.clone();
        layer.strokeWidth = this._strokeWidth;
        layer._pointsRotate = this._pointsRotate;
        
        super.cloneParameter(layer);
    }

    // JSON
    public toJSON()
    {
        var jsonObj = {};
        jsonObj = this.saveParameterToJson(jsonObj);
        return jsonObj;
    }

    public saveParameterToJson(jsonObject)
    {
        jsonObject.controlPoints = this._controlPoints;
        jsonObject.strokeColor = this._strokeColor;
        jsonObject.opacity = this._opacity;
        jsonObject.strokeWidth = this._strokeWidth;
        jsonObject.pointRotate = this._pointsRotate;

        jsonObject = super.saveParameterToJson(jsonObject);

        return jsonObject;
    }

    public static createFromJsonData(jsonData : any) : CurveLayer
    {
        var layer = new CurveLayer();
        layer.loadParameterFromJson(jsonData);
        return layer;
    }

    public loadParameterFromJson(jsonData)
    {
        if(jsonData.controlPoints != null) 
        {
            for(var i = 0; i < jsonData.controlPoints.length; ++i)
            {
                this._controlPoints.push(Vector2.createFromJsonData(jsonData.controlPoints[i]));
            } 
        }
        if(jsonData.strokeColor != null) { this._strokeColor = ColorConstruct.createFromJsonData(jsonData.strokeColor); }
        if(jsonData.opacity != null) { this._opacity = jsonData.opacity; }
        if(jsonData.strokeWidth != null) { this._strokeWidth = jsonData.strokeWidth; }
        if(jsonData.pointRotate != null) { this._pointsRotate = jsonData.pointRotate; }

        super.loadParameterFromJson(jsonData);
    }
}